import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { SITE_URL } from "@/hooks/use-seo";
import { Button } from "@/components/ui/button";
import { Phone, Calendar, CheckCircle2, ArrowRight } from "lucide-react";
import BcbsFederalInfoForm from "@/components/BcbsFederalInfoForm";

const faqs = [
  {
    question: "Is this a treatment consultation?",
    answer:
      "No. This is an educational information session designed to help BCBS Federal Employee Program members and their families learn about newer depression treatment options and insurance considerations.",
  },
  {
    question: "Do I need to be ready to start treatment?",
    answer:
      "No. The session is designed for people who are simply researching and want to better understand their options.",
  },
  {
    question: "Can I attend for my spouse or child?",
    answer:
      "Yes. Many people attend because they are researching treatment options for a covered family member.",
  },
  {
    question: "Does BCBS Federal cover advanced depression treatment?",
    answer:
      "Coverage may be available depending on the individual plan, diagnosis, medical necessity, prior treatment, and authorization requirements. Coverage must be reviewed individually.",
  },
  {
    question: "Will you explain TMS in simple language?",
    answer:
      "Yes. The session is designed for patients and families and avoids unnecessary medical jargon.",
  },
  {
    question: "Is there any obligation to start treatment?",
    answer:
      "No. Requesting information or attending the session does not require you to begin treatment.",
  },
];

const whatWellCover = [
  {
    title: "Why Depression Can Be Difficult to Treat",
    copy: "Learn why some people continue to experience depression even after trying medication, therapy, or other approaches.",
  },
  {
    title: "What Treatment-Resistant Depression Means",
    copy: "Understand this term in simple language and when it may be time to explore additional options.",
  },
  {
    title: "What's New in Depression Treatment",
    copy: "Discover newer non-invasive treatment approaches that may provide another option beyond medication alone.",
  },
  {
    title: "How Accelerated TMS Is Different",
    copy: "Learn how newer TMS approaches may be delivered over a shorter period than traditional TMS.",
  },
  {
    title: "What Treatment Is Actually Like",
    copy: "Understand what patients can expect before, during, and after treatment.",
  },
  {
    title: "Who May Be a Good Candidate",
    copy: "Learn some of the common factors clinicians consider when evaluating treatment options.",
  },
  {
    title: "What Remission Means",
    copy: "Understand the difference between partial improvement and remission from depression symptoms.",
  },
  {
    title: "How Your BCBS Federal Benefits May Help",
    copy: "Learn how benefit verification works, what questions to ask, and why coverage should be reviewed individually.",
  },
  {
    title: "Questions From Patients and Families",
    copy: "The session welcomes questions from both people experiencing depression and family members researching options for someone they care about.",
  },
];

const whoShouldAttend = [
  "You have been dealing with depression despite treatment",
  "A spouse or covered child on your plan is struggling with depression",
  "Medications have not provided enough relief",
  "You want to understand options beyond medication",
  "You have heard about TMS but do not fully understand it",
  "You want to learn what your BCBS Federal benefits may provide",
  "You are researching treatment options for a family member",
  "You simply want to become more informed before making any decisions",
];

const noPressureGoals = [
  "What options exist",
  "How newer treatment works",
  "Who may benefit",
  "What your insurance may help with",
  "What a next step might look like",
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
};

const BCBSFederalDepressionInfoSession = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="BCBS Federal Depression Info Session | Neuro Wellness"
        description="BCBS Federal Employee Program members and families can learn about newer depression treatment options and potential insurance benefits."
        canonical={`${SITE_URL}/bcbs-federal-depression-info-session`}
        schemaJson={faqSchema}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* SECTION 1 — HERO WITH AD BRANDING MATCH */}
        <section className="relative text-primary-foreground py-12 sm:py-20 overflow-hidden bg-gradient-to-br from-[#0284c7] via-[#0369a1] to-[#0f172a]">
          {/* Subtle grid background accent */}
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

          <div className="container-px relative mx-auto max-w-6xl px-4">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left Column: Copy matching Facebook Ad */}
              <div className="lg:col-span-7 space-y-6 text-left">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md px-4 py-1.5 text-xs sm:text-sm font-semibold text-sky-100 border border-white/20">
                  <Calendar className="h-4 w-4 text-amber-300" />
                  <span>Free BCBS Federal Educational Session</span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                  The newest accelerated SAINT® TMS is now available.
                </h1>

                <p className="text-xl sm:text-2xl font-headings text-sky-200 font-bold">
                  Have BCBS Federal Employee Program coverage?
                </p>

                {/* Ad Card Visual Replica */}
                <div className="rounded-2xl bg-white text-slate-900 p-5 sm:p-6 shadow-2xl border border-sky-100 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-700 text-white font-bold text-xs">
                        BCBS
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-sm sm:text-base leading-none">
                          BlueCross BlueShield
                        </div>
                        <div className="text-xs text-slate-500 font-medium">
                          Federal Employee Program®
                        </div>
                      </div>
                    </div>
                    <div className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Government-Wide Service Benefit Plan • PPO
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 items-center">
                    <div className="space-y-1 text-xs sm:text-sm">
                      <div className="text-slate-500">Coverage Status:</div>
                      <div className="font-bold text-slate-900 text-base sm:text-lg">
                        Valued Member Benefits
                      </div>
                    </div>
                    <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-sky-950 font-semibold leading-snug">
                        You may have benefits for advanced depression treatment.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-2 border-t border-slate-100 text-xs text-slate-600">
                    <span className="font-semibold text-slate-900">SAINT®</span>{" "}
                    is an FDA-cleared accelerated TMS treatment for eligible
                    patients with treatment-resistant depression.
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                  <Button
                    asChild
                    size="lg"
                    className="w-full sm:w-auto bg-[#f59e0b] hover:bg-[#d97706] text-slate-950 font-black tracking-wide text-base px-8 py-6 rounded-xl shadow-xl transition-all"
                  >
                    <a href="#info-form">CHECK YOUR BENEFITS &gt;</a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-white/40 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 text-base px-6 py-6 rounded-xl"
                  >
                    <a href="tel:9548279793">
                      <Phone className="h-5 w-5 mr-2" />
                      Call 954-827-9793
                    </a>
                  </Button>
                </div>

                <p className="text-xs text-sky-100/80">
                  Coverage and eligibility vary by plan. Individual results may
                  vary.
                </p>
              </div>

              {/* Right Column: Embedded Quick Lead Form */}
              <div className="lg:col-span-5">
                <div id="quick-form" className="relative">
                  <div className="absolute -top-3 left-6 z-10 bg-amber-400 text-slate-950 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider shadow">
                    Quick Benefits Verification
                  </div>
                  <BcbsFederalInfoForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — DEPRESSION TREATMENT HAS CHANGED */}
        <section className="bg-background py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl px-4 space-y-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-4 leading-tight">
              Depression Treatment Has Come a Long Way
            </h2>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              For many people, depression treatment begins with medication and
              therapy.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              But what happens when those options do not provide enough relief?
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              Today, there are newer non-invasive treatment approaches designed
              for people who continue to struggle with depression despite
              previous treatment.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              During this information session, we will explain these options in
              plain language so you can better understand what may be available
              for you or someone you love.
            </p>
            <div className="pt-2">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#info-form">Get the Session Details</a>
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 3 — WHAT WE'LL COVER */}
        <section className="bg-light-blue/30 py-12 sm:py-16">
          <div className="container-px mx-auto max-w-5xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-6 leading-tight">
              What We'll Cover
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {whatWellCover.map((card, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-card border border-border p-5 shadow-soft space-y-2"
                >
                  <h3 className="font-headings text-base font-bold text-primary leading-snug">
                    {card.title}
                  </h3>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {card.copy}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4 — WHO SHOULD ATTEND */}
        <section className="bg-background py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl px-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-6 leading-tight">
              This Session May Be Helpful If...
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whoShouldAttend.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-xl bg-card border border-border p-4 shadow-soft"
                >
                  <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-foreground/85 text-base leading-relaxed">
                    {item}
                  </span>
                </div>
              ))}
            </div>
            <div className="pt-6">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#info-form">Reserve My Info Session Details</a>
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 5 — FAMILY FOCUS */}
        <section className="bg-light-blue/30 py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl px-4 space-y-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-4 leading-tight">
              You May Be Looking for Answers for Someone You Love
            </h2>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              Depression affects families, not just individuals.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              Many people begin researching treatment because a spouse, son,
              daughter, or other covered dependent is struggling.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              This information session is designed to help families understand
              what newer treatment options exist and what questions they should
              ask.
            </p>
            <div className="pt-2">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#info-form">Get the Session Details</a>
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 6 — NO PRESSURE */}
        <section className="bg-background py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl px-4 space-y-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-4 leading-tight">
              You Don't Have to Make a Treatment Decision
            </h2>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              The purpose of this session is education.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              There is no obligation to begin treatment.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed font-medium">
              The goal is to help you better understand:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {noPressureGoals.map((goal, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-xl bg-card border border-border p-4 shadow-soft"
                >
                  <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-foreground/85 text-base leading-relaxed">
                    {goal}
                  </span>
                </div>
              ))}
            </div>
            <div className="pt-4">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#info-form">Learn More</a>
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 7 — BCBS FEDERAL BENEFITS */}
        <section className="bg-light-blue/30 py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl px-4 space-y-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-4 leading-tight">
              Understand Your BCBS Federal Benefits
            </h2>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              If you have Blue Cross Blue Shield Federal Employee Program
              coverage, behavioral health benefits may be available for eligible
              depression treatment.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              Benefits vary by plan, diagnosis, medical necessity, prior
              treatment history, and authorization requirements.
            </p>
            <p className="text-base sm:text-lg text-foreground/85 leading-relaxed">
              Do not assume a treatment is covered or not covered until your
              individual benefits are reviewed.
            </p>
            <div className="pt-2">
              <Button
                asChild
                size="lg"
                className="bg-[#f59e0b] hover:bg-[#d97706] text-slate-950 font-black tracking-wide text-base px-8 py-6 rounded-xl shadow-lg"
              >
                <a href="#quick-form">CHECK YOUR BENEFITS &gt;</a>
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 8 — SHORT FORM */}
        <section
          id="info-form"
          className="bg-background py-12 sm:py-16 scroll-mt-24"
        >
          <div className="container-px mx-auto max-w-2xl px-4">
            <div className="text-center space-y-2 mb-8">
              <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary leading-tight">
                Get the Info Session Details
              </h2>
              <p className="text-base text-foreground/80 leading-relaxed">
                Fill out the short form and a Neuro Wellness team member will
                send you the information-session details.
              </p>
            </div>
            <BcbsFederalInfoForm />
          </div>
        </section>

        {/* SECTION 9 — FAQ */}
        <section className="bg-light-blue/30 py-12 sm:py-16">
          <div className="container-px mx-auto max-w-3xl px-4 space-y-4">
            <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary mb-2">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((item, idx) => (
                <details
                  key={idx}
                  className="group rounded-xl border border-border bg-card shadow-soft overflow-hidden"
                >
                  <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 font-headings text-base sm:text-lg font-bold text-primary list-none [&::-webkit-details-marker]:hidden">
                    <span>{item.question}</span>
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-accent transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 pb-5 text-foreground/80 text-base leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 10 — FINAL CTA */}
        <section className="bg-gradient-navy py-14 sm:py-20">
          <div className="container-px mx-auto max-w-2xl px-4 text-center space-y-5">
            <h2 className="font-headings text-2xl sm:text-3xl font-bold text-white">
              Get Educated. Understand Your Options.
            </h2>
            <p className="text-primary-foreground/80 text-base sm:text-lg leading-relaxed">
              If depression has been difficult to treat for you or someone in
              your family, this information session can help you understand what
              newer options may be worth exploring.
            </p>
            <p className="text-primary-foreground/80 text-base sm:text-lg leading-relaxed">
              Fill out the short form and we'll send you the session details.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#info-form">Get Info Session Details</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
              >
                <a href="tel:9548279793">
                  <Phone className="h-4 w-4 mr-2" />
                  954-827-9793
                </a>
              </Button>
            </div>
            <p className="text-sm text-primary-foreground/70 pt-2">
              For BCBS Federal Employee Program policyholders, spouses, and
              covered dependents.
            </p>
            <p className="text-xs text-primary-foreground/60 pt-4 max-w-xl mx-auto">
              Individual results vary. Treatment eligibility is determined
              through clinical evaluation. Insurance coverage varies by plan and
              is not guaranteed.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BCBSFederalDepressionInfoSession;
